import { type ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import {
    AzureBlobFSLinkedService,
    AzureSqlDatabaseLinkedService,
    type AzureSqlDWLinkedService,
    LinkedService,
    Metamodel,
    azureBlobFSAuthTypes,
    azureStorageAuthTypes,
    cosmosSqlAuthMethod,
    SQLAuthenticationType,
    SqlServerLinkedService,
    ShellLinkedServiceUtils
} from 'shell-models-lib';
import { AzureBlobStorageLinkedService } from './azure-blob-storage.linkedservice';
import { type AzureSqlMILinkedService } from 'app/model/linkedservice/azure-sql-mi.linkedservice';
import { type AmazonRdsSqlServerLinkedService } from 'app/model/linkedservice/amazon-rds-sql-server.linkedservice';
import { AmazonS3LinkedService } from 'app/model/linkedservice/amazon-s3.linkedservice';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { type AzureCosmosDBLinkedService } from 'app/model/linkedservice/azure-cosmos-db.linkedservice';
import { ConnectionRoleType } from 'app/shared/utils/dataset-utils';
import { GoogleCloudStorageLinkedService } from './google-cloud-storage.linkedservice';


export class LinkedServiceUtils extends ShellLinkedServiceUtils{
    public static isFileSystemTypeInBackend(linkedService: LinkedService): boolean {
        // Backend handles blob storage LS with MI and SPN as ADLS Gen 2 linked services.
        return linkedService instanceof AzureBlobFSLinkedService
            || (linkedService instanceof AzureBlobStorageLinkedService
                && (linkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication
                    || linkedService.authenticationMethod === azureStorageAuthTypes.ServicePrincipalAuthentication
                    || linkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity));
    }

    public static isBucketNameTypeInBackend(linkedService: LinkedService): boolean {
        // Now this is only used by AmazonS3 and AmazonS3 only support source
        return linkedService instanceof AmazonS3LinkedService || linkedService instanceof GoogleCloudStorageLinkedService;
    }

    public static hasSinkTypes(metadata: ILinkedServiceTypeMetadata): boolean {
        const sinkTypes = LinkedServiceUtils.getCopySinkTypes(metadata);
        return sinkTypes && sinkTypes.length > 0;
    }

    public static isSupportedLsForConnectionRole(
        metadata: ILinkedServiceTypeMetadata,
        connectionRole: Extract<ConnectionRoleType, 'source' | 'sink'>
    ): boolean {
        return connectionRole === 'source'
            ? ((metadata.linkedServiceKind === 'file' && !metadata.notSupportCopySource) ||
                (metadata.copySourceTypes && metadata.copySourceTypes.length > 0))
            : ((metadata.linkedServiceKind === 'file' && !metadata.notSupportCopySink) ||
                LinkedServiceUtils.hasSinkTypes(metadata));
    }

    public static getCopySinkTypes(metadata: ILinkedServiceTypeMetadata): string[] {
        return typeof metadata.copySinkTypes === 'function' ? metadata.copySinkTypes() : metadata.copySinkTypes;
    }

    /**
     * Determine whether a linkedService is a 'SqlServerLinkedService based' linkedService
     *
     * @param linkedService
     */
    public static isSqlBasedLinkedService(
        linkedService: unknown
    ): linkedService is (AzureSqlDatabaseLinkedService | AzureSqlMILinkedService | SqlServerLinkedService | AmazonRdsSqlServerLinkedService) {
        // AmazonRdsSqlServerLinkedService inherits SqlServerLinkedService and AzureSqlMILinkedService
        // inherits AzureSqlDatabaseLinkedService. So this is enough.
        return linkedService instanceof AzureSqlDatabaseLinkedService
            || linkedService instanceof SqlServerLinkedService;
    }

    /**
     * Determine whether a linkedService need 'alwaysEncrypted', only 'SqlServerLinkedService based' linkedService may
     * need this.
     *
     * @param linkedService
     */
    public static isLinkedServiceSupportAlwaysEncrypted(linkedService: LinkedService): boolean {
        return this.isSqlBasedLinkedService(linkedService)
            && Boolean(linkedService.alwaysEncrypted);
    }

    /**
     * Return whether the linked service use `Service Principal` auth method.
     * Now only supports `AzureBlobStorage`, `AzureBlobFS`, `AzureSqlDatabase`, `AzureSqlDW`, and `CosmosDb`
     * @param linkedService
     */
    public static isServicePrincipalAuthType(linkedService: LinkedService): boolean {
        switch (Metamodel.modelOf(linkedService).name) {
            case adfModelTypeMap.azureBlob.linkedServiceType:
                return (
                    (linkedService as AzureBlobStorageLinkedService).authenticationMethod ===
                    azureStorageAuthTypes.ServicePrincipalAuthentication
                );
            case adfModelTypeMap.azureBlobFS.linkedServiceType:
                return (
                    (linkedService as AzureBlobFSLinkedService).authenticationMethod ===
                    azureBlobFSAuthTypes.ServicePrincipalAuthentication
                );
            case adfModelTypeMap.azureSql.linkedServiceType:
                return (
                    (linkedService as AzureSqlDatabaseLinkedService).authenticationType ===
                    SQLAuthenticationType.ServicePrincipal
                );
            case adfModelTypeMap.azureSqlDW.linkedServiceType:
                return (
                    (linkedService as AzureSqlDWLinkedService).authenticationType ===
                    SQLAuthenticationType.ServicePrincipal
                );
            case adfModelTypeMap.cosmosSql.linkedServiceType:
                return (
                    (linkedService as AzureCosmosDBLinkedService).authenticationMethod ===
                    cosmosSqlAuthMethod.servicePrincipal
                );
            default:
                return false;
        }
    }

    /**
     * Return whether the linked service use `User Assigned Managed Identity` auth method.
     * Now only supports `AzureBlobFS`, `AzureSqlDatabase`, `AzureSqlDW`, and `CosmosDb`
     * @param linkedService
     */
    public static isUAMIAuthType(linkedService: LinkedService): boolean {
        switch (Metamodel.modelOf(linkedService).name) {
            case adfModelTypeMap.azureBlobFS.linkedServiceType:
                return (linkedService as AzureBlobFSLinkedService).authenticationMethod === azureBlobFSAuthTypes.UserAssignedManagedIdentity;
            case adfModelTypeMap.azureSql.linkedServiceType:
                return (linkedService as AzureSqlDatabaseLinkedService).authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity;
            case adfModelTypeMap.azureSqlDW.linkedServiceType:
                return (linkedService as AzureSqlDWLinkedService).authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity;
            case adfModelTypeMap.cosmosSql.linkedServiceType:
                return (linkedService as AzureCosmosDBLinkedService).authenticationMethod === cosmosSqlAuthMethod.userAssigedManagedIdentity;
            default:
                return false;
        }
    }
}
